import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import api from '../api/axios'
import { Building2, X, Mail, Phone, Tag, Plus, Pencil, Trash2, Search, ChevronUp, ChevronDown, Star, Pin, Send, FileText, BookOpen } from 'lucide-react'
import ClientGuideline from '../components/ClientGuideline'
import { useTheme } from '../context/ThemeContext'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'

function Toggle({ value, onChange }: { value: boolean, onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${value ? 'bg-orange-500' : 'bg-gray-200'}`}>
      <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${value ? 'left-[3px]' : 'left-[19px]'}`} />
    </button>
  )
}

function AddSubscriptionForm({ clientId, onAdded, renewMode }: { clientId: string, onAdded: () => void, renewMode?: boolean }) {
  const [show, setShow] = useState(false)
  const [plans, setPlans] = useState<any[]>([])
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (show) {
      api.get('/settings/plans').then(res => {
        const active = res.data.filter((p: any) => p.isActive)
        setPlans(active)
        if (active.length > 0) setSelectedPlanId(active[0].planId)
      }).catch(() => {})
    }
  }, [show])

  const handleAdd = async () => {
    if (!selectedPlanId) return
    setSaving(true)
    try {
      await api.post(`/clients/${clientId}/subscription`, { planId: selectedPlanId })
      onAdded()
      setShow(false)
    } catch {}
    setSaving(false)
  }

  const selectedPlan = plans.find(p => p.planId === selectedPlanId)

  if (!show) return (
    <button onClick={() => setShow(true)} className="text-sm text-orange-500 hover:text-orange-600 font-medium transition-colors">
      {renewMode ? '↻ Renew / Change Plan' : '+ Add subscription'}
    </button>
  )

  return (
    <div className="space-y-3 mt-2">
      <select className="input py-2 text-sm" value={selectedPlanId} onChange={e => setSelectedPlanId(e.target.value)}>
        {plans.map(p => (
          <option key={p.planId} value={p.planId}>
            {p.planName} — {p.jobCount} jobs / {p.periodDays} days {p.price ? `($${p.price})` : '(Free)'}
          </option>
        ))}
      </select>
      {selectedPlan && (
        <p className="text-xs text-gray-400">{selectedPlan.description}</p>
      )}
      <div className="flex gap-2">
        <button onClick={() => setShow(false)} className="btn-ghost text-xs py-1.5 flex-1">Cancel</button>
        <button onClick={handleAdd} disabled={saving || !selectedPlanId} className="btn-primary text-xs py-1.5 flex-1 disabled:opacity-50">
          {saving ? 'Saving...' : renewMode ? 'Renew / Change Plan' : 'Add Subscription'}
        </button>
      </div>
    </div>
  )
}

function PortalAccessSection({ client, onSilentRefresh }: { client: any, onSilentRefresh: () => void }) {
  const [portalEnabled, setPortalEnabled] = useState(client.portalAccessEnabled || false)

  useEffect(() => {
    setPortalEnabled(client.portalAccessEnabled || false)
  }, [client.portalAccessEnabled])
  const [credentials, setCredentials] = useState<{ username: string, oneTimePassword: string } | null>(null)
  const [editPassword, setEditPassword] = useState('')
  const [showEditPassword, setShowEditPassword] = useState(false)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  const handleToggle = async (enable: boolean) => {
    setToggling(true)
    setError('')
    setCredentials(null)
    try {
      const res = await api.post(`/clients/${client.clientId}/portal-access`, { enable })
      setPortalEnabled(enable)
      if (enable && res.data.username) {
        setCredentials({ username: res.data.username, oneTimePassword: res.data.oneTimePassword })
      }
      onSilentRefresh()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to toggle portal access')
    }
    setToggling(false)
  }

  const handleResetPassword = async () => {
    if (!editPassword || editPassword.length < 6) {
      setError('Password must be at least 8 characters')
      return
    }
    setResetting(true)
    setError('')
    try {
      await api.patch(`/clients/${client.clientId}/portal-password`, { newPassword: editPassword })
      setCredentials(prev => prev ? { ...prev, oneTimePassword: editPassword } : null)
      setShowEditPassword(false)
      setEditPassword('')
      setSuccessMsg('Password updated')
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to reset password')
    }
    setResetting(false)
  }

  const handleSendWelcomeEmail = async () => {
    setSendingEmail(true)
    setError('')
    try {
      await api.post(`/clients/${client.clientId}/send-welcome-email`, {
        username: credentials?.username || client.username,
        password: credentials?.oneTimePassword || ''
      })
      setSuccessMsg('Welcome email sent successfully')
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send welcome email')
    }
    setSendingEmail(false)
  }

  return (
    <div className="border border-gray-100 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-navy-800">Portal Access</p>
          <p className="text-xs text-gray-400 mt-0.5">Allow client to log into the portal</p>
        </div>
        <div className="flex items-center gap-2">
          {toggling && <span className="text-xs text-gray-400">Updating...</span>}
          <button
            type="button"
            onClick={() => handleToggle(!portalEnabled)}
            disabled={toggling}
            className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${portalEnabled ? 'bg-orange-500' : 'bg-gray-200'}`}>
            <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${portalEnabled ? 'left-[22px]' : 'left-[3px]'}`} />
          </button>
        </div>
      </div>

      {error && <p className="text-red-500 text-xs">{error}</p>}
      {successMsg && <p className="text-green-600 text-xs">{successMsg}</p>}

      {portalEnabled && (
        <div className="space-y-3 pt-1">
          {/* Credentials display */}
          {credentials ? (
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-orange-700 mb-1">Login Credentials</p>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Username</span>
                <span className="font-mono font-semibold text-navy-800">{credentials.username}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Password</span>
                <span className="font-mono font-semibold text-navy-800">{credentials.oneTimePassword}</span>
              </div>
              <p className="text-[10px] text-orange-500 mt-1">⚠ Share these credentials with the client. Password shown once.</p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400">Portal access is enabled. Reset password to generate new credentials.</p>
              {client.username && (
                <div className="flex justify-between text-xs mt-2">
                  <span className="text-gray-500">Username</span>
                  <span className="font-mono font-semibold text-navy-800">{client.username}</span>
                </div>
              )}
            </div>
          )}

          {/* Reset password */}
          {!showEditPassword ? (
            <button
              onClick={() => setShowEditPassword(true)}
              className="text-xs text-orange-500 hover:text-orange-700 font-medium transition-colors">
              Reset password
            </button>
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                className="input text-sm"
                placeholder="Enter new password (min 8 chars)"
                value={editPassword}
                onChange={e => setEditPassword(e.target.value)}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowEditPassword(false); setEditPassword('') }}
                  className="btn-ghost text-xs py-1.5 flex-1">
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={resetting}
                  className="btn-primary text-xs py-1.5 flex-1">
                  {resetting ? 'Saving...' : 'Save Password'}
                </button>
              </div>
            </div>
          )}

          {/* Send welcome email */}
          {client.email && (
            <button
              onClick={handleSendWelcomeEmail}
              disabled={sendingEmail}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-navy-800 hover:bg-navy-900 text-white text-xs font-medium transition-colors">
              {sendingEmail ? 'Sending...' : '✉ Send Welcome Email to Client'}
            </button>
          )}
          {!client.email && (
            <p className="text-xs text-red-400">No email address on file — update client email first to send welcome email.</p>
          )}
        </div>
      )}
    </div>
  )
}

function ClientDetailModal({ client, onClose, onUpdated, onSilentRefresh }: {
  client: any, onClose: () => void, onUpdated: () => void, onSilentRefresh?: () => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const { isDark } = useTheme()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    name: client.name, email: client.email || '',
    phone: client.phone || '', companyName: client.companyName || '',
    abbreviation: client.abbreviation || '',
    artistRate: client.artistRate || '', qcRate: client.qcRate || '',
    notes: client.notes || '', status: client.status,
    timezone: client.timezone || ''
  })
  const [saving, setSaving] = useState(false)
  const [confirmDeactivate, setConfirmDeactivate] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'activity' | 'feedback'>('details')
  const [clientFeedback, setClientFeedback] = useState<any[]>([])
  const [loadingFeedback, setLoadingFeedback] = useState(false)
  const [jobHistory, setJobHistory] = useState<any>(null)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [activity, setActivity] = useState<any[]>([])
  const [loadingActivity, setLoadingActivity] = useState(false)
  const [activityPage, setActivityPage] = useState(1)
  const activityPerPage = 8
  const [jobSummary, setJobSummary] = useState<any>(null)
  const [notes, setNotes] = useState<any[]>([])
  const [newNote, setNewNote] = useState('')
  const [newNoteColor, setNewNoteColor] = useState('gray')
  const [addingNote, setAddingNote] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)
  const [showGuideline, setShowGuideline] = useState(false)

  const fetchNotes = () => {
    api.get(`/clients/${client.clientId}/notes`).then(res => setNotes(res.data)).catch(() => {})
  }

  useEffect(() => { fetchNotes() }, [client.clientId])

  useEffect(() => {
    api.get(`/jobs?clientId=${client.clientId}&limit=200`).then(res => {
      const jobs = res.data.jobs || []
      const active = jobs.filter((j: any) => !['Completed', 'Dismissed'].includes(j.status))
      const completed = jobs.filter((j: any) => j.status === 'Completed')
      const lastJob = jobs[0]
      setJobSummary({
        active: active.length,
        completed: completed.length,
        total: res.data.pagination?.total || jobs.length,
        lastJobDate: lastJob ? new Date(lastJob.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null,
        lastJobTitle: lastJob?.displayTitle || null
      })
    }).catch(() => {})
  }, [client.clientId])

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }))

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await api.patch(`/clients/${client.clientId}`, form)
      onUpdated()
      setEditing(false)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async () => {
    try {
      await api.patch(`/clients/${client.clientId}`, { status: 'inactive' })
      onUpdated()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to deactivate')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-modal w-full max-w-md max-h-[90vh] overflow-y-auto animate-modal-in">
        <div className="p-6 border-b border-gray-100 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-display font-bold text-lg">{client.name[0]}</span>
            </div>
            <div>
              <h2 className="font-display font-bold text-navy-900 text-lg">{client.name}</h2>
              <span className="font-mono text-xs text-gray-400">{client.clientCode}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-navy-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <Pencil size={15} />
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={15} />
            </button>
          </div>
        </div>

        {!editing && (
          <div className="flex border-b border-gray-100">
            <button onClick={() => setActiveTab('details')}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'details' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-navy-700'}`}>
              Details
            </button>
            <button onClick={async () => {
              setActiveTab('history')
              if (!jobHistory) {
                setLoadingHistory(true)
                try {
                  const res = await api.get(`/jobs/client-history/${client.clientId}`)
                  setJobHistory(res.data)
                } catch {}
                setLoadingHistory(false)
              }
            }}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'history' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-navy-700'}`}>
              Job History {jobHistory && <span className="text-xs text-gray-400 ml-1">({jobHistory.total})</span>}
            </button>
            <button onClick={async () => {
              setActiveTab('activity')
              setLoadingActivity(true)
              try {
                const res = await api.get(`/clients/${client.clientId}/activity-log`)
                setActivity(res.data || [])
              } catch {}
              setLoadingActivity(false)
            }}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'activity' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-navy-700'}`}>
              Activity
            </button>
            <button onClick={async () => {
              setActiveTab('feedback')
              if (clientFeedback.length === 0) {
                setLoadingFeedback(true)
                try {
                  const res = await api.get(`/jobs/feedback/all?clientId=${client.clientId}`)
                  setClientFeedback(res.data)
                } catch {}
                setLoadingFeedback(false)
              }
            }}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'feedback' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-navy-700'}`}>
              <Star size={13} className="inline mr-1 text-yellow-500" />Feedback {clientFeedback.length > 0 && <span className="text-xs text-gray-400 ml-1">({clientFeedback.length})</span>}
            </button>
          </div>
        )}
        <div className="p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl border border-red-100">{error}</div>}

          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Name</label>
                  <input className="input" value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                <div>
                  <label className="label">Company</label>
                  <input className="input" value={form.companyName} onChange={e => set('companyName', e.target.value)} />
                </div>
                <div>
                  <label className="label">Abbreviation <span className="text-gray-400 font-normal">(used in job codes, e.g. STR)</span></label>
                  <input className="input uppercase" maxLength={10} placeholder="e.g. STR"
                    value={form.abbreviation} onChange={e => set('abbreviation', e.target.value.toUpperCase())} />
                </div>
              </div>
              <div>
                <label className="label">Email</label>
                <input className="input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div>
                <label className="label">Phone</label>
                <input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Artist Rate</label>
                  <input className="input" type="number" step="0.1" value={form.artistRate} onChange={e => set('artistRate', e.target.value)} />
                </div>
                <div>
                  <label className="label">QC Rate</label>
                  <input className="input" type="number" step="0.1" value={form.qcRate} onChange={e => set('qcRate', e.target.value)} />
                </div>
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="label">Timezone</label>
                <select className="input" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                  <option value="">No timezone set</option>
                  <optgroup label="US Timezones">
                    <option value="America/New_York">Eastern (ET) — New York</option>
                    <option value="America/Chicago">Central (CT) — Chicago</option>
                    <option value="America/Denver">Mountain (MT) — Denver</option>
                    <option value="America/Phoenix">Mountain no DST — Phoenix</option>
                    <option value="America/Los_Angeles">Pacific (PT) — Los Angeles</option>
                    <option value="America/Anchorage">Alaska (AKT) — Anchorage</option>
                    <option value="Pacific/Honolulu">Hawaii (HT) — Honolulu</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="Europe/London">GMT — London</option>
                    <option value="Europe/Paris">CET — Paris</option>
                    <option value="Asia/Dubai">GST — Dubai</option>
                    <option value="Asia/Singapore">SGT — Singapore</option>
                    <option value="Australia/Sydney">AEDT — Sydney</option>
                  </optgroup>
                </select>
              </div>
              <div>
                {/* Notes field removed — use Client Guideline instead */}
              </div>
              <div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditing(false)} className="btn-ghost flex-1">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : activeTab === 'activity' ? (
            <div className="space-y-1">
              {loadingActivity ? (
                <div className="py-12 text-center text-gray-400 text-sm">Loading...</div>
              ) : activity.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">No portal activity recorded yet</div>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-100 dark:bg-[#1e3a5f]" />
                  <div className="space-y-1">
                    {activity.slice((activityPage - 1) * activityPerPage, activityPage * activityPerPage).map((event: any) => {
                      const icons: Record<string, any> = {
                        login: { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>, color: '#3b82f6', label: 'Logged in to portal' },
                        job_view: { icon: <FileText size={13} />, color: '#f97316', label: `Viewed job ${event.metadata?.jobCode || ''}` },
                        feedback_submitted: { icon: <Star size={13} />, color: '#eab308', label: `Rated job ${event.metadata?.jobCode || ''} ${'★'.repeat(event.metadata?.rating || 0)}` },
                        page_visit: { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, color: '#10b981', label: `Visited ${event.metadata?.page || 'portal'}` },
                      }
                      const info = icons[event.eventType] || { icon: null, color: '#9ca3af', label: event.eventType }
                      return (
                        <div key={event.id} className="flex items-start gap-3 pl-10 pr-2 py-2.5 relative hover:bg-gray-50 dark:hover:bg-[#112240] rounded-xl transition-colors">
                          <div className="absolute left-2.5 top-3 w-3 h-3 rounded-full border-2 border-white dark:border-[#16304f] flex items-center justify-center"
                            style={{ backgroundColor: info.color }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-navy-800 dark:text-white truncate flex items-center gap-1.5">
                              <span className="text-gray-400 flex-shrink-0">{info.icon}</span>
                              {info.label}
                            </p>
                            {event.metadata?.jobTitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{event.metadata.jobTitle}</p>}
                          </div>
                          <p className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">
                            {new Date(event.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {activity.length > activityPerPage && (
                <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-[#1e3a5f]">
                  <button onClick={() => setActivityPage(p => Math.max(1, p - 1))} disabled={activityPage === 1}
                    className="text-xs text-gray-500 hover:text-navy-700 disabled:opacity-30 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">← Prev</button>
                  <span className="text-xs text-gray-400">Page {activityPage} of {Math.ceil(activity.length / activityPerPage)}</span>
                  <button onClick={() => setActivityPage(p => Math.min(Math.ceil(activity.length / activityPerPage), p + 1))} disabled={activityPage === Math.ceil(activity.length / activityPerPage)}
                    className="text-xs text-gray-500 hover:text-navy-700 disabled:opacity-30 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">Next →</button>
                </div>
              )}
            </div>
          ) : activeTab === 'history' ? (
            <div className="space-y-4">
              {loadingHistory ? (
                <div className="py-12 text-center text-gray-400 text-sm">Loading...</div>
              ) : !jobHistory ? (
                <div className="py-12 text-center text-gray-400 text-sm">No data</div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Jobs per Day — Last 30 Days</p>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={jobHistory.byDay} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={6} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backgroundColor: isDark ? '#1c3a5e' : '#ffffff', color: isDark ? '#e2e8f0' : '#1e293b' }} labelStyle={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                        <Bar dataKey="total" fill="#f97316" radius={[2, 2, 0, 0]} name="Jobs" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Fresh vs Revision — Last 30 Days</p>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={jobHistory.byDayStacked} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={6} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backgroundColor: isDark ? '#1c3a5e' : '#ffffff', color: isDark ? '#e2e8f0' : '#1e293b' }} labelStyle={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                        <Bar dataKey="fresh" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} name="Fresh" />
                        <Bar dataKey="revision" stackId="a" fill="#3b82f6" radius={[2, 2, 0, 0]} name="Revision" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Jobs per Month — Last 12 Months</p>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={jobHistory.byMonth} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backgroundColor: isDark ? '#1c3a5e' : '#ffffff', color: isDark ? '#e2e8f0' : '#1e293b' }} labelStyle={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[2, 2, 0, 0]} name="Jobs" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'feedback' ? (
            <div className="space-y-3">
              {loadingFeedback ? (
                <div className="py-12 text-center text-gray-400 text-sm">Loading...</div>
              ) : clientFeedback.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="mb-2"><Star size={28} className="text-yellow-400 mx-auto" /></div>
                  <p className="text-sm text-gray-400">No feedback yet</p>
                </div>
              ) : (
                <>
                  {/* Average rating + trend */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/10 rounded-2xl border border-yellow-100 dark:border-yellow-900/30">
                    <div className="text-3xl font-bold text-yellow-500">
                      {(clientFeedback.reduce((s, f) => s + f.rating, 0) / clientFeedback.length).toFixed(1)}
                    </div>
                    <div className="flex-1">
                      <div className="flex gap-0.5 mb-1">
                        {[1,2,3,4,5].map(i => (
                          <span key={i} className={`text-lg ${i <= Math.round(clientFeedback.reduce((s, f) => s + f.rating, 0) / clientFeedback.length) ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400">{clientFeedback.length} review{clientFeedback.length > 1 ? 's' : ''}</p>
                    </div>
                    {/* Distribution */}
                    <div className="space-y-0.5">
                      {[5,4,3,2,1].map(r => {
                        const count = clientFeedback.filter(f => f.rating === r).length
                        const pct = Math.round((count / clientFeedback.length) * 100)
                        return (
                          <div key={r} className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-400 w-2">{r}</span>
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[10px] text-gray-400 w-4">{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  {/* Individual feedback */}
                  {clientFeedback.map(f => (
                    <div key={f.id} className="bg-gray-50 dark:bg-[#112240] rounded-xl px-4 py-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(i => (
                            <span key={i} className={`text-sm ${i <= f.rating ? 'text-yellow-400' : 'text-gray-200 dark:text-gray-700'}`}>★</span>
                          ))}
                        </div>
                        <p className="text-[10px] text-gray-400">{new Date(f.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                      <p className="text-xs font-mono text-orange-400">{f.jobCode}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-300 truncate">{f.jobTitle}</p>
                      {f.comment && <p className="text-xs text-gray-500 dark:text-gray-400 italic">"{f.comment}"</p>}
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : (
            <>
            {/* Client Guideline */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-navy-800 dark:text-white flex items-center gap-2">
                  <BookOpen size={14} className="text-orange-500" /> Guideline
                </p>
                <button onClick={() => setShowGuideline(true)}
                  className="flex items-center gap-1.5 text-xs text-orange-500 hover:text-orange-600 bg-orange-50 dark:bg-orange-900/10 px-3 py-1.5 rounded-lg font-medium transition-colors border border-orange-100 dark:border-orange-900/20">
                  <BookOpen size={11} /> View / Edit
                </button>
              </div>
              {/* Pinned Notes */}
              {notes.length > 0 && (
                <div className="space-y-2 mb-4">
                  {notes.filter(n => n.isPinned).map(n => (
                    <div key={n.id} className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border ${
                      n.color === 'red' ? 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30' :
                      n.color === 'green' ? 'bg-green-50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30' :
                      n.color === 'yellow' ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-100 dark:border-yellow-900/30' :
                      'bg-gray-50 dark:bg-[#112240] border-gray-100 dark:border-[#1e3a5f]'
                    }`}>
                      <Pin size={13} className="text-orange-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-navy-800 dark:text-gray-200">{n.content}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{n.createdBy}</p>
                      </div>
                      {!editing && (
                        <div className="flex gap-1">
                          <button onClick={async () => { await api.patch(`/clients/${client.clientId}/notes/${n.id}`, { isPinned: false }); fetchNotes() }}
                            className="text-[10px] text-gray-400 hover:text-gray-600">unpin</button>
                          <button onClick={async () => { await api.delete(`/clients/${client.clientId}/notes/${n.id}`); fetchNotes() }}
                            className="text-[10px] text-red-400 hover:text-red-600"><X size={10} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                  {notes.filter(n => !n.isPinned).map(n => (
                    <div key={n.id} className="flex items-start gap-2 px-3 py-2 rounded-xl bg-gray-50 dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f]">
                      <div className="flex-1">
                        <p className="text-xs text-navy-800 dark:text-gray-200">{n.content}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{n.createdBy} · {new Date(n.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                      </div>
                      {!editing && (
                        <div className="flex gap-1">
                          <button onClick={async () => { await api.patch(`/clients/${client.clientId}/notes/${n.id}`, { isPinned: true }); fetchNotes() }}
                            className="text-[10px] text-gray-400 hover:text-orange-500">pin</button>
                          <button onClick={async () => { await api.delete(`/clients/${client.clientId}/notes/${n.id}`); fetchNotes() }}
                            className="text-[10px] text-red-400 hover:text-red-600"><X size={10} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Note */}
              {!editing && (
                <div className="mb-4">
                  {!showAddNote ? (
                    <button onClick={() => setShowAddNote(true)} className="text-xs text-orange-500 hover:text-orange-600 font-medium">+ Add note</button>
                  ) : (
                    <div className="bg-gray-50 dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-xl p-3 space-y-2">
                      <textarea className="input resize-none text-xs" rows={2} value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." />
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {['gray', 'red', 'green', 'yellow'].map(c => (
                            <button key={c} type="button" onClick={() => setNewNoteColor(c)}
                              className={`w-4 h-4 rounded-full border-2 ${newNoteColor === c ? 'border-navy-500' : 'border-transparent'} ${
                                c === 'red' ? 'bg-red-400' : c === 'green' ? 'bg-green-400' : c === 'yellow' ? 'bg-yellow-400' : 'bg-gray-400'
                              }`} />
                          ))}
                        </div>
                        <div className="flex gap-2 ml-auto">
                          <button onClick={() => { setShowAddNote(false); setNewNote('') }} className="text-xs text-gray-400">Cancel</button>
                          <button onClick={async () => {
                            if (!newNote.trim()) return
                            setAddingNote(true)
                            await api.post(`/clients/${client.clientId}/notes`, { content: newNote, color: newNoteColor, isPinned: false })
                            setNewNote('')
                            setNewNoteColor('gray')
                            setShowAddNote(false)
                            fetchNotes()
                            setAddingNote(false)
                          }} disabled={addingNote} className="text-xs bg-orange-500 text-white px-3 py-1 rounded-lg font-medium disabled:opacity-50">
                            {addingNote ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Job Summary Bar */}
              {jobSummary && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-orange-50 dark:bg-orange-900/10 rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-orange-500">{jobSummary.active}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Active</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/10 rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-green-500">{jobSummary.completed}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Completed</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-blue-500">{jobSummary.total}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Total</p>
                  </div>
                </div>
              )}
              {client.companyName && (
                <div className="flex items-center gap-3 text-sm">
                  <Building2 size={15} className="text-gray-400" />
                  <span className="text-navy-700">{client.companyName}</span>
                </div>
              )}
              {client.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail size={15} className="text-gray-400" />
                  <span className="text-navy-700">{client.email}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone size={15} className="text-gray-400" />
                  <span className="text-navy-700">{client.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <Tag size={15} className="text-gray-400" />
                <span className="text-navy-700 capitalize">{client.entryType?.replace('_', ' ')}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-orange-50 rounded-xl p-4">
                  <p className="text-xs text-orange-400 font-medium mb-1">Artist Rate</p>
                  <p className="text-lg font-display font-bold text-orange-600">{client.artistRate}</p>
                </div>
                <div className="bg-navy-50 rounded-xl p-4">
                  <p className="text-xs text-navy-400 font-medium mb-1">QC Rate</p>
                  <p className="text-lg font-display font-bold text-navy-600">{client.qcRate}</p>
                </div>
              </div>

              {/* Subscription */}
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-navy-800">Subscription</p>
                  {client.activeSubscription ? (
                      <div className="flex items-center gap-1.5">
                        <span className="bg-green-50 text-green-600 text-xs px-2 py-0.5 rounded-full font-medium">Active</span>
                        {client.activeSubscription.isTrial && (
                          <span className="bg-green-50 text-green-600 text-xs px-2 py-0.5 rounded-full font-medium">Trial</span>
                        )}
                      </div>
                  ) : (
                    <span className="bg-gray-100 text-gray-400 text-xs px-2 py-0.5 rounded-full font-medium">None</span>
                  )}
                </div>
                {client.activeSubscription ? (
                  <div className="space-y-2">
                    {/* Plan name + features */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-semibold text-orange-500">{client.activeSubscription.planName || 'Custom Plan'}</p>
                        {client.activeSubscription.isTrial && (
                          <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full font-medium">Trial</span>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap justify-end">
                        {client.activeSubscription.unlimitedRevisions || client.activeSubscription.revisionTopupActive ? (
                          <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full font-medium">∞ Revisions</span>
                        ) : (
                          <span className="text-[10px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded-full font-medium">{client.activeSubscription.freeRevisions} Rev</span>
                        )}
                        {client.activeSubscription.complexJobsAllowed > 0 && (
                          <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium">{client.activeSubscription.complexJobsAllowed} Complex</span>
                        )}
                      </div>
                    </div>
                    {/* Jobs progress */}
                    <div>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-gray-400">Jobs</span>
                        <span className="font-medium text-navy-700">{client.activeSubscription.jobsUsed} / {client.activeSubscription.totalJobsAllowed}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="bg-orange-500 h-1.5 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (client.activeSubscription.jobsUsed / client.activeSubscription.totalJobsAllowed) * 100)}%` }} />
                      </div>
                    </div>
                    {/* Revisions progress */}
                    {!client.activeSubscription.unlimitedRevisions && !client.activeSubscription.revisionTopupActive && client.activeSubscription.freeRevisions > 0 && (
                      <div>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="text-gray-400">Revisions</span>
                          <span className="font-medium text-navy-700">{client.activeSubscription.revisionsUsed} / {client.activeSubscription.freeRevisions}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div className="bg-blue-400 h-1.5 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (client.activeSubscription.revisionsUsed / client.activeSubscription.freeRevisions) * 100)}%` }} />
                        </div>
                      </div>
                    )}
                    {/* Complex jobs progress */}
                    {client.activeSubscription.complexJobsAllowed > 0 && (
                      <div>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="text-gray-400">Complex</span>
                          <span className="font-medium text-navy-700">{client.activeSubscription.complexJobsUsed} / {client.activeSubscription.complexJobsAllowed}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div className="bg-amber-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (client.activeSubscription.complexJobsUsed / client.activeSubscription.complexJobsAllowed) * 100)}%` }} />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Started {new Date(client.activeSubscription.startDate).toLocaleDateString()}</span>
                      <span>Expires {new Date(client.activeSubscription.endDate).toLocaleDateString()}</span>
                    </div>
                    <AddSubscriptionForm clientId={client.clientId} onAdded={onUpdated} renewMode={true} />
                  </div>
                ) : (
                  <AddSubscriptionForm clientId={client.clientId} onAdded={onUpdated} />
                )}
              </div>
              {client.notes && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-400 font-medium mb-1">Notes</p>
                  <p className="text-sm text-navy-700">{client.notes}</p>
                </div>
              )}

              {/* Portal Access Section */}
              <PortalAccessSection client={client} onSilentRefresh={onSilentRefresh || onUpdated} />
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${client.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                  {client.status}
                </span>
                <span className="text-xs text-gray-400">
                  {client.totalJobs || 0} total jobs
                  {client.activeSubscription && ` · ${client.activeSubscription.jobsUsed}/${client.activeSubscription.totalJobsAllowed} this period`}
                </span>
              </div>
              <div className="border-t border-gray-100 pt-3">
                {!confirmDeactivate ? (
                  <button onClick={() => setConfirmDeactivate(true)} className="flex items-center gap-2 text-red-400 hover:text-red-600 text-sm font-medium transition-colors">
                    <Trash2 size={14} /> Deactivate client
                  </button>
                ) : (
                  <div className="bg-red-50 rounded-xl p-4 space-y-3">
                    <p className="text-sm text-red-700 font-medium">Deactivate {client.name}?</p>
                    <div className="flex gap-2">
                      <button onClick={() => setConfirmDeactivate(false)} className="btn-ghost text-xs py-1.5 flex-1">Cancel</button>
                      <button onClick={handleDeactivate} className="bg-red-500 hover:bg-red-600 text-white text-xs py-1.5 px-3 rounded-xl flex-1 font-medium transition-colors">
                        Yes, deactivate
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showGuideline && (
        <ClientGuideline
          clientId={client.clientId.toString()}
          clientName={client.companyName || client.name || ''}
          open={showGuideline}
          onClose={() => setShowGuideline(false)}
        />
      )}
    </div>
  )
}

function NewClientModal({ onClose, onCreated }: { onClose: () => void, onCreated: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const [form, setForm] = useState<any>({
    name: '', email: '', phone: '', companyName: '',
    abbreviation: '',
    entryType: 'email_client', artistRate: '', qcRate: '',
    password: '', notes: '',
    hasSubscription: false, subscriptionPeriod: 'monthly', totalJobsAllowed: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const set = (key: string, value: string) => setForm((f: any) => ({ ...f, [key]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await api.post('/clients', form)
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create client')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm animate-backdrop-in" onClick={onClose} />  
      <div className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] flex flex-col animate-modal-in">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h2 className="font-display font-bold text-navy-900 text-xl">New Client</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="John Smith" />
            </div>
            <div>
              <label className="label">Email *</label>
              <input className="input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required placeholder="john@company.com" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Phone</label>
              <input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 98765 43210" />
            </div>
            <div>
              <label className="label">Company Name</label>
              <input className="input" value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Acme Studios" />
            </div>
            <div>
              <label className="label">Abbreviation <span className="text-gray-400 font-normal">(used in job codes, e.g. STR)</span></label>
              <input className="input uppercase" maxLength={10} placeholder="e.g. STR"
                value={form.abbreviation} onChange={e => set('abbreviation', e.target.value.toUpperCase())} />
            </div>
          </div>
          <div>
            <label className="label">Entry Type *</label>
            <div className="flex gap-3">
              {['email_client', 'portal_client'].map(type => (
                <button key={type} type="button" onClick={() => set('entryType', type)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${form.entryType === type ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-200 text-gray-500 hover:border-orange-300'}`}>
                  {type === 'email_client' ? 'Email Client' : 'Portal Client'}
                </button>
              ))}
            </div>
          </div>
          {form.entryType === 'portal_client' && (
            <div>
              <label className="label">Password *</label>
              <input className="input" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Artist Rate *</label>
              <input className="input" type="number" step="0.1" value={form.artistRate} onChange={e => set('artistRate', e.target.value)} required placeholder="1.0" />
            </div>
            <div>
              <label className="label">QC Rate *</label>
              <input className="input" type="number" step="0.1" value={form.qcRate} onChange={e => set('qcRate', e.target.value)} required placeholder="0.5" />
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any special instructions..." />
          </div>

          {/* Subscription section */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-navy-800">Subscription</p>
                <p className="text-xs text-gray-400">Enable if this client has a job quota</p>
              </div>
              <Toggle value={form.hasSubscription} onChange={(v: boolean) => setForm((f: any) => ({ ...f, hasSubscription: v }))} />
            </div>
            {form.hasSubscription && (
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="label">Period *</label>
                  <select className="input" value={form.subscriptionPeriod} onChange={e => set('subscriptionPeriod', e.target.value)}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="label">Job Count *</label>
                  <input className="input" type="number" min="1" value={form.totalJobsAllowed} onChange={e => set('totalJobsAllowed', e.target.value)} placeholder="10" />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-50">
              {loading ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function ClientsPage() {
  const [clients, setClients] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<any>(null)
  const [showNewForm, setShowNewForm] = useState(false)
  const [showDeactivated, setShowDeactivated] = useState(false)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table')
  const fetchClients = () => {
    setLoading(true)
    api.get('/clients').then(res => {
      setClients(res.data)
      setSelected((prev: any) => {
        if (!prev) return null
        return res.data.find((c: any) => c.clientId === prev.clientId) || prev
      })
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  useEffect(() => { fetchClients() }, [])

  const filtered = clients.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.clientCode.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.companyName?.toLowerCase().includes(search.toLowerCase())
    const matchType = filterType === 'all' || 
  (filterType === 'trial' ? c.entryType === 'trial' : c.entryType === filterType)
    return matchSearch && matchType && c.status === 'active'
  })

  const deactivated = clients.filter(c => c.status === 'inactive')

  const kanbanGroups = [
    {
      key: 'trial',
      label: 'Trial',
      color: 'border-green-200 dark:border-green-700/30',
      headerColor: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
      clients: filtered.filter(c => c.activeSubscription?.isTrial || c.entryType === 'trial')
    },
    {
      key: 'paid',
      label: 'Paid',
      color: 'border-blue-200 dark:border-blue-700/30',
      headerColor: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
      clients: filtered.filter(c => c.activeSubscription && !c.activeSubscription.isTrial && c.entryType !== 'trial')
    },
    {
      key: 'noplan',
      label: 'No Plan',
      color: 'border-gray-200 dark:border-gray-700/30',
      headerColor: 'bg-gray-50 dark:bg-[#1a3050] text-gray-600 dark:text-gray-300',
      clients: filtered.filter(c => !c.activeSubscription)
    },
    {
      key: 'expired',
      label: '⚠ Expired / Inactive',
      color: 'border-red-200 dark:border-red-700/30',
      headerColor: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300',
      clients: deactivated
    },
  ]

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">Clients</h1>
          <p className="text-gray-400 mt-1">{filtered.length} of {clients.length} clients</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 dark:bg-[#1a3050] rounded-xl p-1">
            <button onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${viewMode === 'table' ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
              ☰ Table
            </button>
            <button onClick={() => setViewMode('kanban')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${viewMode === 'kanban' ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
              ⊞ Kanban
            </button>
          </div>
          <button onClick={() => setShowNewForm(true)} className="btn-primary flex items-center gap-2">
            <Plus size={15} /> New Client
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-9 py-2 text-sm"
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
            <select className="input py-2 text-sm w-40" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="email_client">Email Client</option>
              <option value="portal_client">Portal Client</option>
              <option value="trial">Trial Clients</option>
            </select>
            <button
              onClick={() => setFilterType('trial')}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${filterType === 'trial' ? 'bg-green-500 border-green-500 text-white' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
              Trial Clients
            </button>
          </div>
      </div>

      {loading ? (
        <div className="card overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50 dark:border-[#1a3050]">
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-40" />
                <div className="skeleton h-3 w-24" />
              </div>
              <div className="skeleton h-6 w-20 rounded-full hidden sm:block" />
              <div className="skeleton h-4 w-28 hidden sm:block" />
              <div className="skeleton h-6 w-16 rounded-full hidden sm:block" />
            </div>
          ))}
        </div>
      ) : viewMode === 'kanban' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {kanbanGroups.map(group => (
            <div key={group.key} className={`border ${group.color} rounded-2xl overflow-hidden`}>
              <div className={`px-4 py-3 ${group.headerColor} flex items-center justify-between`}>
                <p className="text-xs font-bold uppercase tracking-wide">{group.label}</p>
                <span className="text-xs font-semibold opacity-70">{group.clients.length}</span>
              </div>
              <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
                {group.clients.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-6">No clients</p>
                ) : (
                  group.clients.map(client => (
                    <div key={client.clientId} onClick={() => setSelected(client)}
                      className="bg-white dark:bg-[#0d1e35] rounded-xl p-3 cursor-pointer hover:shadow-md transition-all border border-gray-100 dark:border-[#1e3a5f] hover:border-orange-200 dark:hover:border-orange-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-orange-600 dark:text-orange-400 font-semibold text-xs">{client.name[0]}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-navy-800 dark:text-white truncate">{client.name}</p>
                          <p className="text-xs font-mono text-gray-400 truncate">{client.clientCode}</p>
                        </div>
                      </div>
                      {client.companyName && (
                        <p className="text-xs text-gray-400 truncate mb-1">{client.companyName}</p>
                      )}
                      {client.activeSubscription ? (() => {
                        const sub = client.activeSubscription
                        const daysLeft = Math.max(0, Math.ceil((new Date(sub.endDate).getTime() - Date.now()) / 86400000))
                        const jobsLeft = sub.totalJobsAllowed - sub.jobsUsed
                        const jobPct = Math.min(100, (sub.jobsUsed / sub.totalJobsAllowed) * 100)
                        return (
                          <div className="mt-2 space-y-1.5">
                            {/* Jobs progress bar */}
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] text-gray-400">Jobs</span>
                                <span className="text-[10px] font-medium text-navy-700 dark:text-white">{sub.jobsUsed} / {sub.totalJobsAllowed} <span className="text-gray-400 font-normal">({jobsLeft} left)</span></span>
                              </div>
                              <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${jobPct >= 90 ? 'bg-red-400' : jobPct >= 70 ? 'bg-orange-400' : 'bg-green-400'}`}
                                  style={{ width: `${jobPct}%` }} />
                              </div>
                            </div>
                            {/* Days + revisions row */}
                            <div className="flex items-center justify-between">
                              <span className={`text-[10px] font-medium ${daysLeft <= 7 ? 'text-red-500' : daysLeft <= 15 ? 'text-orange-500' : 'text-gray-400'}`}>
                                {daysLeft}d left
                              </span>
                              {!sub.unlimitedRevisions && !sub.revisionTopupActive ? (
                                <span className="text-[10px] text-gray-400">{sub.revisionsUsed ?? 0} / {sub.freeRevisions} rev</span>
                              ) : (
                                <span className="text-[10px] text-blue-500">∞ rev</span>
                              )}
                            </div>
                          </div>
                        )
                      })() : (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400">{client.totalJobs || 0} jobs</span>
                          {client.avgRating && (
                            <span className="text-xs font-medium text-yellow-500 flex items-center gap-0.5">
                              <Star size={10} className="fill-yellow-400 text-yellow-400" />{client.avgRating}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Client</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Company</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Type</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Jobs</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Artist Rate</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Rating</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Status</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden md:table-cell">Subscription</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(client => (
                <tr key={client.clientId} onClick={() => setSelected(client)} className="hover:bg-orange-50/40 transition-colors cursor-pointer group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-600 font-semibold text-xs">{client.name[0]}</span>
                      </div>
                      <div>
                        <p className="font-medium text-navy-800 group-hover:text-orange-500 transition-colors">{client.name}</p>
                        <p className="text-xs font-mono text-gray-400">{client.clientCode}{client.abbreviation ? ` · ${client.abbreviation}` : ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-400 hidden sm:table-cell">{client.companyName || '—'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        client.entryType === 'portal_client' ? 'bg-blue-50 text-blue-600' : 
                        client.entryType === 'trial' ? 'bg-green-50 text-green-600' : 
                        'bg-gray-100 text-gray-500'}`}>
                        {client.entryType === 'portal_client' ? '🌐 Portal' : 
                        client.entryType === 'trial' ? 'Trial' : 'Email'}
                      </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-navy-600">{client.totalJobs || 0} jobs</td>
                  <td className="px-6 py-4 font-mono text-xs text-navy-600 hidden sm:table-cell">{client.artistRate}</td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    {client.avgRating ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-yellow-500">
                        <Star size={11} className="fill-yellow-400 text-yellow-400" />
                        {client.avgRating} <span className="text-gray-400 font-normal">({client.totalRatings})</span>
                      </span>
                    ) : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${client.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell">
                    {client.activeSubscription ? (() => {
                      const sub = client.activeSubscription
                      const daysLeft = Math.max(0, Math.ceil((new Date(sub.endDate).getTime() - Date.now()) / 86400000))
                      const jobsLeft = sub.totalJobsAllowed - sub.jobsUsed
                      const jobPct = Math.min(100, (sub.jobsUsed / sub.totalJobsAllowed) * 100)
                      return (
                        <div className="space-y-1 min-w-[160px]">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">{sub.jobsUsed}/{sub.totalJobsAllowed} jobs</span>
                            <span className="text-xs text-gray-400">{jobsLeft} left</span>
                          </div>
                          <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${jobPct >= 90 ? 'bg-red-400' : jobPct >= 70 ? 'bg-orange-400' : 'bg-green-400'}`}
                              style={{ width: `${jobPct}%` }} />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={`text-[10px] font-medium ${daysLeft <= 7 ? 'text-red-500' : daysLeft <= 15 ? 'text-orange-500' : 'text-gray-400'}`}>
                              {daysLeft}d remaining
                            </span>
                            {!sub.unlimitedRevisions && !sub.revisionTopupActive ? (
                              <span className="text-[10px] text-gray-400">{sub.revisionsUsed ?? 0}/{sub.freeRevisions} rev</span>
                            ) : (
                              <span className="text-[10px] text-blue-500">∞ revisions</span>
                            )}
                          </div>
                        </div>
                      )
                    })() : (
                      <span className="text-xs text-gray-300">No subscription</span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-16 text-center text-gray-400">No clients found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Deactivated Clients Section */}
      {deactivated.length > 0 && (
        <div className="mt-8">
          <button onClick={() => setShowDeactivated(v => !v)}
            className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors mb-3">
            {showDeactivated ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            Deactivated Clients ({deactivated.length})
          </button>
          {showDeactivated && (
            <div className="card overflow-hidden opacity-75">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/70">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Client</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Type</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Code</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {deactivated.map(client => (
                    <tr key={client.clientId} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelected(client)}>
                      <td className="px-6 py-3.5">
                        <p className="font-medium text-gray-500">{client.name}</p>
                        {client.companyName && <p className="text-xs text-gray-400">{client.companyName}</p>}
                      </td>
                      <td className="px-6 py-3.5 text-xs text-gray-400 capitalize">{client.entryType}</td>
                      <td className="px-6 py-3.5 text-xs text-gray-400">{client.email}</td>
                      <td className="px-6 py-3.5 text-xs font-mono text-gray-400">{client.clientCode}</td>
                      <td className="px-6 py-3.5">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-400">inactive</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selected && createPortal(<ClientDetailModal client={selected} onClose={() => setSelected(null)} onUpdated={() => { fetchClients(); setSelected(null) }} onSilentRefresh={() => { fetchClients() }} />, document.body)}
      {showNewForm && createPortal(<NewClientModal onClose={() => setShowNewForm(false)} onCreated={fetchClients} />, document.body)}
    </div>
  )
}